import { useTranslation } from "react-i18next";
import { SearchIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useMediaQuery } from "usehooks-ts";
import { cn } from "@/utils/cn";

interface SearchTriggerProps {
  onOpen: () => void;
}

export const SearchTrigger = ({ onOpen }: SearchTriggerProps) => {
  const { t } = useTranslation("common");
  // Full search input only on extra-large screens; everything smaller uses the icon.
  const isLargeScreen = useMediaQuery("(min-width: 1440px)");
  const isMobile = !isLargeScreen;

  return (
    <TooltipProvider delayDuration={400}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size={isMobile ? "icon" : "sm"}
            onClick={onOpen}
            aria-label={t("common:a11y.search_the_app")}
            className={cn(
              "rounded-xs border border-border bg-surface shadow-sm transition-all duration-200",
              "hover:border-primary hover:shadow-md hover:ring-1 hover:ring-primary/20",
              isMobile
                ? "size-9 bg-surface text-primary hover:bg-primary-muted"
                : // Button wraps children in a shrink-to-fit span; widen it so the
                  // label's flex-1 can push the / hint to the far right edge.
                  "h-9 w-60 justify-start gap-2.5 px-2.5 text-muted-foreground hover:text-foreground lg:w-72 [&>span]:w-full [&>span]:justify-start [&>span]:gap-2.5",
            )}
          >
            {isMobile ? (
              <SearchIcon className="size-4" />
            ) : (
              <>
                <span className="flex size-6 shrink-0 items-center justify-center rounded-sxs bg-primary-muted text-primary">
                  <SearchIcon className="size-3.5" />
                </span>
                <span className="flex-1 text-start text-sm">
                  {t("header.search_page_placeholder")}
                </span>
                <kbd className="shrink-0 rounded-sxs border border-border bg-background px-1.5 py-0.5 font-mono text-[10px] leading-none text-muted-foreground shadow-sm">
                  /
                </kbd>
              </>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {isMobile ? t("common:buttons.search") : t("common:chrome.press_to_search")}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
